import React from 'react'
import PropTypes from 'prop-types'
import OutlinedInput from '@material-ui/core/TextField'
import FormControl from '@material-ui/core/FormControl'
import { makeStyles } from '@material-ui/core/styles'
import { Tooltip } from '@material-ui/core';
import { useTextField } from './useTextField'
import { LabelOutside } from './LabelOutside'
import { HelperTextOutside } from './HelperTextOutside'
const useTextFieldStyles = makeStyles(theme => ({
    root: {
        '& .MuiInputLabel-animated': {
            color: theme.palette.grey[600]
        },
        '& .MuiSvgIcon-root': {
            padding: 0,
            fill: theme.palette.grey[600],
            '& .MuiSvgIcon-fontSizeSmall': {
                width: '10.8px',
                height: '6.67px'
            },
            '& .MuiSvgIcon-fontSizeLarge': {
                width: '14.4px',
                height: '8.89px'
            }
        }
    },
    inputBase: {
        '& input[type=number]': {
            '-moz-appearance': 'textfield'
        },
        '& input[type=number]::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0
        },
        '& input[type=number]::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0
        },
        '& .MuiSvgIcon-root:hover': {
            color: '#999999',
        },
        '& .MuiFormLabel-root': {
            color: theme.palette.grey[600]
        },
        '& .MuiInputBase-input ': {
            color: theme.palette.grey[700],
            fontSize: '14px',
            fontWeight: 400,
            paddingLeft: '12px',
            paddingRight: 0,
            lineHeight: '19px',
            paddingTop: '15px',
            paddingBottom: '16px',
            height: 'unset',
            textOverflow: 'ellipsis',
            caretColor: ({isSearchable}) => !isSearchable ? 'transparent' : 'black',
            cursor: ({isSearchable}) => !isSearchable ? 'default' : 'text',   
        },
        '& .MuiFormLabel-root.Mui-error': {
            color: theme.palette.grey[500]
        },
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            fontWeight:'600',
            transform: 'translate(14px, -6px) scale(0.75)',
            paddingLeft: '8px',
            paddingRight: '8px',
            left: '-6px'
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: '5px',
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.grey[600],
            cursor: ({isSearchable}) => !isSearchable ? 'default' : 'text',   
        },
        '& .MuiOutlinedInput-adornedEnd': {
            paddingRight: '10px',
        },
        '& .MuiOutlinedInput-inputMarginDense': {
            paddingTop: '10px',
            paddingBottom: '9px',
        },
        '& .MuiTypography-root': {
            color: ({isDisabled}) => isDisabled ? theme.palette.grey[400] : theme.palette.grey[600],
        },
        '& .MuiInputBase-input.Mui-disabled':{
            color: theme.palette.grey[400],
            backgroundColor: `${theme.palette.grey[200]} !important`,
        },
        '& .MuiInputBase-root.Mui-disabled': {
            backgroundColor: `${theme.palette.grey[200]} !important`,
            cursor: 'no-drop',
            color: theme.palette.grey[400],
            '& .MuiInputAdornment-root': {
                color: theme.palette.grey[400],
            },
            '& .MuiOutlinedInput-multiline': {
                padding: 0
            }
        },
        '&:hover': {
            borderRadius: '5px',
            border: `1px solid ${theme.palette.primary.main}`
        },
        '& .active': {
            border: `1px solid ${theme.palette.primary.main}`
        },
        '& .Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
                boxShadow: ({dropdown}) => dropdown? 'none' : `0px 0px 5px 1px rgba(76, 27, 155, 0.45)`,
                borderRadius: '5px',
                border: `1px solid ${theme.palette.primary.main}`
            },
        },
        '& .Mui-error': {
            '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                    boxShadow: 'none',
                    borderRadius: '5px',
                    border: `1px solid ${theme.palette.error.main}`
                },
            },
            '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                    boxShadow: 'none',
                    borderRadius: '5px',
                    border: `1px solid ${theme.palette.error.main}`
                },
            },
        },
        '& .PrivateNotchedOutline-legendNotched': {
            maxWidth: '1000px',
            transition: 'max-width 100ms cubic-bezier(0.0, 0, 0.2, 1) 50ms',
            backgroundColor: theme.palette.grey[50]
        },
        '&:focus': {
            border: `1px solid ${theme.palette.primary.main}`,
            color: theme.palette.grey[100],
            borderRadius: '5px'
        },
        '& .MuiInputAdornment-positionStart': {
            marginRight: '-6px',
            width: '1.0625rem'
        }

},
    readOnly: {
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: 'transparent'
            },
            '&:hover fieldset': {
                borderColor: 'transparent'
            },
            '&.Mui-focused fieldset': {
                borderColor: 'transparent'
            }
        }
    }
}))

// eslint-disable-next-line react/display-name
const TextField = React.forwardRef(({
    onChange,
    value,
    dropdown,
    maxLength,
    showCharCounter,
    rows,
    isSearchable = true,
    InputProps,
    tooltip = {title: ''},
    ...props},
    ref,
    ) => {
        
    const classes = useTextFieldStyles({dropdown, isSearchable, isDisabled: props.disabled})
    const { buttonState, labelOutsideState, helperState } = useTextField(props, classes)

    let minWidthIcon 
    if (helperState.icon) {
        minWidthIcon = '84px'
    } else {
        minWidthIcon = '62px'
    }
    
    const onChangeHandler = (e) => {
        onChange(e)
    }

    const calculateMaxHeight = (sizeFactorInPixels, paramRows) => {
        if (!paramRows) return `${sizeFactorInPixels}px`
        
        const sizeCalc = sizeFactorInPixels * paramRows;
        return `${sizeCalc}px`
    }
    return (
        <div className={props.className}>
            <Tooltip placement={tooltip.placement} title={tooltip.title}>
                <FormControl style={{width:'100%'}}>
                    <LabelOutside {...labelOutsideState}/>
                    <OutlinedInput
                        id={`TextField-custom-${props?.key}`}
                        variant="outlined"
                        {...buttonState}
                        onChange={onChangeHandler}
                        value={value}
                        inputRef={ref}
                        style={{
                            width:'100%',
                            minWidth: helperState.prefix ? '100%' : minWidthIcon,
                            maxHeight: props?.size === 'small' ? calculateMaxHeight(38, rows) : calculateMaxHeight(50, rows),
                            maxWidth: '100%',
                            border: 'none',
                        }}
                        autoComplete={props?.autoComplete}
                        inputProps={{maxLength, style: rows && {padding: 0, fontSize: '13px'}, ...buttonState.inputProps}}
                        multiline={rows !== undefined}
                        rows={rows}
                        InputProps={InputProps ? InputProps : buttonState.InputProps}
                    />
                    <div style={{
                        display: 'flex',
                        justifyContent: helperState.helperText ? 'space-between' : 'end',
                    }} className='helper-text-div'>
                    {!buttonState.InputProps?.readOnly && <HelperTextOutside {...helperState} 
                        style={{
                            fontSize: helperState?.size === 'small' ? '11px' : '15px',
                            lineHeight: helperState?.size === 'small' ? '15px' : '18px',
                            marginTop: '4px'
                        }} />}
                    {maxLength && showCharCounter && <div style={{
                            fontSize: '11px',
                            lineHeight: '15px',
                            marginTop: '4px',
                            marginRight: 0,
                            color: '#707070',
                        }} >{value ? value.length : '0'}/{maxLength}</div>}
                    </div>
                </FormControl>
            </Tooltip>
        </div>
    )
})

TextField.defaultProps = {
    label: '',
    showLabelOptional: false,
    labelInside: false,
    readOnly: false,
    size: 'small',
    prefix: '',
    suffix: '',
    error: false
}

TextField.propTypes = {
    size: PropTypes.oneOf(['small', 'large']),
    className: PropTypes.string,
}

export default TextField